import jsPDF from "jspdf";

const CRIMSON = [217, 45, 32];
const DARK = [26, 26, 26];
const GRAY = [96, 96, 96];
const BOX_BG = [247, 247, 248];
const BOX_BORDER = [230, 230, 232];
const SOLUTION_BG = [253, 237, 235];
const SOLUTION_BORDER = [242, 190, 184];

export function exportMagnetPdf(magnet) {
  if (!magnet) return;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 54;
  const maxW = pageW - margin * 2;
  const bottom = pageH - margin - 26;
  let y = margin;

  const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const norm = (s) => (s || "").replace(/[^a-zà-ÿ0-9]/gi, "").toLowerCase();
  const ensure = (needed) => {
    if (y + needed > bottom) { addFooter(); doc.addPage(); y = margin; }
  };
  const isHeading = (t) => {
    const s = t.trim();
    if (!s) return false;
    const letters = s.replace(/[^A-Za-zÀ-ÿ]/g, "");
    return letters.length >= 3 && s === s.toUpperCase() && s.length <= 64;
  };
  const isOption = (t) => /^\s*[a-eA-E][\)\.]\s+/.test(t) || /^\s*[-•]\s+/.test(t);
  const isQuestion = (lines) => /^\s*\d+[\.\)]/.test(lines[0] || "");
  const isSolution = (lines) =>
    lines.some((l) => /(^|\s)(E ADESSO|COSA POTETE FARE|COSA FARE|PROSSIMI PASSI)/i.test(l.trim()) && isHeading(l));

  const addFooter = () => {
    const fy = pageH - margin - 4;
    doc.setDrawColor(BOX_BORDER[0], BOX_BORDER[1], BOX_BORDER[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, fy - 14, pageW - margin, fy - 14);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); setColor(GRAY);
    doc.text("Seguimi su Instagram", margin, fy);
    doc.setFont("helvetica", "bold"); setColor(CRIMSON);
    doc.text("@massimilianopulla", margin + doc.getTextWidth("Seguimi su Instagram") + 7, fy);
    // page number
    doc.setFont("helvetica", "normal"); setColor(GRAY); doc.setFontSize(8.5);
    const pn = String(doc.internal.getNumberOfPages());
    doc.text(pn, pageW - margin - doc.getTextWidth(pn), fy);
  };

  // ---- Header brandizzato ----
  doc.setFillColor(DARK[0], DARK[1], DARK[2]); doc.rect(0, 0, pageW, 96, "F");
  doc.setFillColor(CRIMSON[0], CRIMSON[1], CRIMSON[2]); doc.rect(0, 0, pageW, 5, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(255, 255, 255);
  doc.text("MASSIMILIANO PULLA", margin, 44, { charSpace: 1.6 });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(185, 185, 185);
  doc.text("RISORSA GRATUITA PER LA TUA COPPIA", margin, 64, { charSpace: 1.2 });
  y = 96 + 38;

  // ---- Titolo ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(22); setColor(DARK);
  const titleLines = doc.splitTextToSize(magnet.titolo || "Risorsa gratuita", maxW);
  for (const l of titleLines) { doc.text(l, margin, y); y += 26; }
  y += 2;

  // ---- Pill formato ----
  if (magnet.formato) {
    const label = String(magnet.formato).toUpperCase();
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5);
    const tw = doc.getTextWidth(label);
    doc.setFillColor(CRIMSON[0], CRIMSON[1], CRIMSON[2]);
    doc.roundedRect(margin, y - 10, tw + 22, 19, 9.5, 9.5, "F");
    doc.setTextColor(255, 255, 255); doc.text(label, margin + 11, y + 2.5);
    y += 30;
  } else y += 6;

  const lineH = 16;

  // ---- Blocchi separati da righe vuote (salta i separatori '---') ----
  const rawLines = String(magnet.contenuto || "").replace(/\r/g, "").split("\n");
  const blocks = []; let cur = [];
  for (const ln of rawLines) {
    const t = ln.trim();
    if (t === "" || /^-{3,}$/.test(t)) { if (cur.length) { blocks.push(cur); cur = []; } }
    else cur.push(ln);
  }
  if (cur.length) blocks.push(cur);
  // salta un eventuale primo blocco che ripete il titolo
  if (blocks.length && blocks[0].length && norm(blocks[0][0]) === norm(magnet.titolo)) {
    blocks[0] = blocks[0].slice(1);
    if (!blocks[0].length) blocks.shift();
  }

  const wrap = (text, w, size, style) => {
    doc.setFont("helvetica", style); doc.setFontSize(size);
    return doc.splitTextToSize(text.trim(), w);
  };

  const drawSectionHeading = (text) => {
    ensure(84);
    y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(12.5); setColor(CRIMSON);
    doc.text(text.trim(), margin, y);
    y += 8;
    doc.setDrawColor(CRIMSON[0], CRIMSON[1], CRIMSON[2]); doc.setLineWidth(2);
    doc.line(margin, y, margin + 32, y);
    y += 18;
  };

  // Misura + disegna un blocco "box" (domanda o soluzione)
  const drawBoxedBlock = (lines, bg, border) => {
    const innerW = maxW - 28;
    const items = lines.map((ln) => {
      const heading = isHeading(ln);
      const option = !heading && isOption(ln);
      const style = heading ? "bold" : "normal";
      const size = heading ? 11.5 : 11;
      const w = option ? innerW - 14 : innerW;
      const wrapped = wrap(ln, w, size, style);
      return { wrapped, heading, option, size, lh: heading ? lineH + 2 : (option ? lineH - 1 : lineH) };
    });
    const pad = 16;
    const innerH = items.reduce((h, it) => h + it.wrapped.length * it.lh + (it.heading ? 4 : 0) + (it.option ? 2 : 3), 0);
    const boxH = innerH + pad * 2 - 4;
    const drawBox = boxH <= bottom - margin;
    // se il box ci sta in una pagina, garantiamo che l'INTERO box entri: nessuna interruzione dentro al box
    if (drawBox) ensure(boxH + 10);
    const boxY = y;
    if (drawBox) {
      doc.setFillColor(bg[0], bg[1], bg[2]); doc.setDrawColor(border[0], border[1], border[2]); doc.setLineWidth(0.7);
      doc.roundedRect(margin, boxY - pad + 4, maxW, boxH, 7, 7, "FD");
    }
    y = boxY + 6;
    for (const it of items) {
      if (it.heading) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(11.5); setColor(CRIMSON); y += 4;
      } else if (it.option) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(11); setColor(DARK);
      } else {
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); setColor(DARK);
      }
      const x = margin + 14 + (it.option ? 14 : 0);
      for (let k = 0; k < it.wrapped.length; k++) {
        if (!drawBox) ensure(it.lh); // solo nel fallback (blocco più alto di una pagina)
        doc.text(it.wrapped[k], x, y);
        y += it.lh;
      }
      y += it.option ? 2 : 3;
    }
    y += 16;
  };

  const drawPlainBlock = (lines) => {
    for (const ln of lines) {
      if (isHeading(ln)) { drawSectionHeading(ln); continue; }
      const wrapped = wrap(ln, maxW, 11, "normal");
      doc.setFont("helvetica", "normal"); doc.setFontSize(11); setColor(GRAY);
      for (const line of wrapped) { ensure(lineH); doc.text(line, margin, y); y += lineH; }
    }
    y += 12;
  };

  for (const b of blocks) {
    if (!b.length) continue;
    if (isSolution(b)) drawBoxedBlock(b, SOLUTION_BG, SOLUTION_BORDER);
    else if (isQuestion(b)) drawBoxedBlock(b, BOX_BG, BOX_BORDER);
    else drawPlainBlock(b);
  }

  addFooter();

  const name = (magnet.titolo || "risorsa").replace(/[^a-z0-9]+/gi, "_").slice(0, 48);
  doc.save(`${name}.pdf`);
}
